import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { UiTextField, UiIcon, UiLabel } from 'ui-components';
import { usePermission, useOnClickOutside, useRouter } from 'hooks';
import { fetchTagCategories } from 'store';
import { getCurrentSlice } from 'utils';
import { TagFilterDropdown, TagFilterSelectedList } from './components';
import { StyledContentTagFilter } from './ContentTagFilter.styles';

export const ContentTagFilter = (props) => {
  const { componentName, parentPlatform, className } = props;
  const filter = useSelector((state) =>
    parentPlatform
      ? state[state.filterReducer.filtersContext].filters[parentPlatform][
          componentName
        ]
      : state[state.filterReducer.filtersContext].filters[componentName]
  );
  const reducerName = useSelector(
    (state) => state.filterReducer.filtersContext
  );
  const tagCategories = useSelector((state) => state.tagReducer.tagCategories);
  const { selectedIds = [], warning_label } = filter || {};
  const dispatch = useDispatch();
  const { setSelectedFilterIds } = getCurrentSlice(reducerName);
  const { clearRouterFilters } = useRouter();
  const [currentTag, setCurrentTag] = useState('');
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const { userPermission } = usePermission();
  const tagFilterContentRef = useRef(null);
  const { t } = useTranslation();

  useOnClickOutside(tagFilterContentRef, () => setIsDropdownVisible(false));

  const { permission: hasTaggedContentUpdatePermission } = useMemo(
    () => userPermission('TaggedContent', 'update'),
    [userPermission]
  );

  useEffect(() => {
    if (hasTaggedContentUpdatePermission) {
      dispatch(fetchTagCategories());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectTag = (id) => {
    dispatch(
      setSelectedFilterIds({
        id,
        isChecked: true,
        componentName,
        parentPlatform,
      })
    );
    setIsDropdownVisible(false);
    setCurrentTag('');
    clearRouterFilters();
  };

  const handleRemoveTag = (id) => {
    dispatch(
      setSelectedFilterIds({
        id,
        isChecked: false,
        componentName,
        parentPlatform,
      })
    );
    clearRouterFilters();
  };

  if (!hasTaggedContentUpdatePermission) return null;

  return (
    <StyledContentTagFilter>
      {warning_label && (
        <UiLabel size={10} color='#F16063'>
          {t(warning_label)}
        </UiLabel>
      )}
      <div
        className={classNames('filter-content-tag', className)}
        ref={tagFilterContentRef}
      >
        <UiTextField
          value={currentTag}
          onChange={(event) => {
            setCurrentTag(event.target.value);
          }}
          onClick={() => setIsDropdownVisible(true)}
          className='category-name-input'
          placeholder={t('content:enterTagName')}
          data-testid='filter-content-tag-label'
          InputProps={{
            startAdornment: (
              <UiIcon icon='ri-search-line' className='search-icon' />
            ),
            'data-testid': 'filter-content-tag-input',
          }}
        />
        {isDropdownVisible && tagCategories.data.length > 0 && (
          <TagFilterDropdown
            currentTag={currentTag}
            tagCategories={tagCategories.data}
            selectedIds={selectedIds}
            onSelectedTagAdd={handleSelectTag}
          />
        )}
        {selectedIds.length > 0 && (
          <TagFilterSelectedList
            tagCategories={tagCategories.data}
            selectedIds={selectedIds}
            onSelectedTagRemove={handleRemoveTag}
          />
        )}
      </div>
    </StyledContentTagFilter>
  );
};

ContentTagFilter.propTypes = {
  componentName: PropTypes.string.isRequired,
  parentPlatform: PropTypes.string,
  className: PropTypes.string,
};
